import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import ZoomableImage from "./ZoomableImage";
import FullScreenZoomModal from "./FullScreenZoomModal";

const ImageCarousel = ({
  autoPlayOnHover = false,
  showThumbs = false,
  showIndicators = false,
  ImagesArr = [],
  autoPlay = false,
  hero = false,
  className = "",
  zoomOnHover = false,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [fullScreenModalOpen, setFullScreenModalOpen] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const containerRef = useRef(null);

  const [hasOverflow, setHasOverflow] = useState(ImagesArr.length > 3);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(ImagesArr.length > 3);

  const checkScrollState = () => {
    if (!containerRef.current) return;
    const wrapper = containerRef.current.querySelector(".carousel .thumbs-wrapper");
    if (wrapper) {
      const overflow = wrapper.scrollWidth > wrapper.clientWidth + 4;
      setHasOverflow(overflow);
      setCanScrollLeft(wrapper.scrollLeft > 4);
      setCanScrollRight(overflow && wrapper.scrollLeft < wrapper.scrollWidth - wrapper.clientWidth - 4);
    }
  };

  useEffect(() => {
    if (!showThumbs || !containerRef.current) return;
    const wrapper = containerRef.current.querySelector(".carousel .thumbs-wrapper");
    if (!wrapper) return;

    checkScrollState();
    const timer = setTimeout(checkScrollState, 200);
    wrapper.addEventListener("scroll", checkScrollState, { passive: true });
    window.addEventListener("resize", checkScrollState);

    // Prevent react-easy-swipe from blocking native touch scrolling on mobile
    const handleTouchMove = (e) => {
      e.stopPropagation();
    };

    // Support touch / mouse drag swipe scrolling
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    const handleMouseDown = (e) => {
      isDown = true;
      startX = e.pageX;
      scrollStart = wrapper.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      const diff = e.pageX - startX;
      if (Math.abs(diff) > 4) {
        wrapper.scrollLeft = scrollStart - diff;
      }
    };

    const handleMouseUp = () => {
      isDown = false;
      checkScrollState();
    };

    wrapper.addEventListener("touchmove", handleTouchMove, { capture: true, passive: true });
    wrapper.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      clearTimeout(timer);
      wrapper.removeEventListener("scroll", checkScrollState);
      window.removeEventListener("resize", checkScrollState);
      wrapper.removeEventListener("touchmove", handleTouchMove, { capture: true });
      wrapper.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [showThumbs, ImagesArr]);

  const handleScrollThumbs = (direction) => {
    if (!containerRef.current) return;
    const wrapper = containerRef.current.querySelector(".carousel .thumbs-wrapper");
    if (wrapper) {
      const scrollAmount = 180;
      wrapper.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
      setTimeout(checkScrollState, 300);
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const carouselKey = ImagesArr.map((item) =>
    typeof item === "string" ? item : item?.url || ""
  ).join(";");

  return (
    <div
      ref={containerRef}
      className={`image-carousel-container ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Carousel
        key={carouselKey}
        preventMovementUntilSwipeScrollTolerance={true}
        swipeScrollTolerance={50}
        infiniteLoop={true}
        interval={autoPlayOnHover ? 1000 : hero ? 5000 : 3000}
        autoPlay={autoPlayOnHover ? isHovering : autoPlay}
        showArrows={autoPlayOnHover ? false : !hero}
        showStatus={false}
        showThumbs={showThumbs}
        showIndicators={showIndicators}
        emulateTouch={true}
        renderArrowPrev={
          hero
            ? (onClickHandler, _hasPrev, label) => (
                <button
                  type="button"
                  onClick={onClickHandler}
                  title={label}
                  className="hero-nav-btn prev"
                  aria-label={label}
                >
                  <ChevronLeftIcon />
                </button>
              )
            : undefined
        }
        renderArrowNext={
          hero
            ? (onClickHandler, _hasNext, label) => (
                <button
                  type="button"
                  onClick={onClickHandler}
                  title={label}
                  className="hero-nav-btn next"
                  aria-label={label}
                >
                  <ChevronRightIcon />
                </button>
              )
            : undefined
        }
        selectedItem={currentSlideIndex}
        onChange={(index) => {
          setCurrentSlideIndex(index);
          if (containerRef.current) {
            const videos = containerRef.current.querySelectorAll("video");
            videos.forEach((v) => {
              try {
                v.pause();
                v.currentTime = 0;
              } catch (e) {}
            });
          }
        }}
        renderThumbs={() =>
          ImagesArr.map((item, index) => {
            const isString = typeof item === "string";
            const media = isString ? { type: "image", url: item } : item;
            const isVideo = Boolean(media && media.type === "video");
            const isImage = !isVideo;
            if (isImage) {
              return (
                <img
                  key={`thumb-${index}`}
                  src={isString ? item : media.url}
                  alt={`Thumb ${index + 1}`}
                />
              );
            }
            if (isVideo) {
              // Use poster if available, otherwise show a simple play indicator
              return media.poster ? (
                <div key={`thumb-${index}`} style={{ position: "relative" }}>
                  <img
                    src={media.poster}
                    alt={`Video ${index + 1}`}
                    style={{ display: "block" }}
                  />
                  {/* Play badge overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "rgba(0, 0, 0, 0.45)",
                      borderRadius: "50%",
                      width: 30,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 16,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    }}
                  >
                    ▶
                  </div>
                </div>
              ) : (
                <div
                  key={`thumb-${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "1rem",
                  }}
                >
                  ▶
                </div>
              );
            }
            return null;
          })
        }
      >
        {ImagesArr.map((item, index) => {
          const isString = typeof item === "string";
          const media = isString ? { type: "image", url: item } : item;
          const isVideo = Boolean(media && media.type === "video");
          const isImage = !isVideo;
          const link = media?.link;
          const isExternal = link && (link.startsWith("http://") || link.startsWith("https://"));

          const slideMedia = (
            <>
              {isImage &&
                (zoomOnHover ? (
                  <ZoomableImage
                    src={isString ? item : media.url}
                    loading={index === 0 || zoomOnHover ? "eager" : "lazy"}
                    fetchpriority={index === 0 ? "high" : "auto"}
                    decoding="async"
                    alt={media.title || `Image ${index + 1}`}
                    onClick={() => {
                      setCurrentSlideIndex(index);
                      setFullScreenModalOpen(true);
                    }}
                  />
                ) : (
                  <img
                    src={isString ? item : media.url}
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchpriority={index === 0 ? "high" : "low"}
                    decoding="async"
                    alt={media.title || `Image ${index + 1}`}
                  />
                ))}
              {isVideo && (
                <video
                  src={media.url}
                  controls
                  playsInline
                  poster={media.poster}
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "75vh",
                    objectFit: "contain",
                    backgroundColor: "#000",
                  }}
                />
              )}
            </>
          );

          return (
            <div key={index}>
              {link ? (
                isExternal ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "block", textDecoration: "none" }}
                  >
                    {slideMedia}
                  </a>
                ) : (
                  <Link
                    to={link}
                    style={{ display: "block", textDecoration: "none" }}
                  >
                    {slideMedia}
                  </Link>
                )
              ) : (
                slideMedia
              )}
            </div>
          );
        })}
      </Carousel>

      {showThumbs && ImagesArr.length > 1 && hasOverflow && (
        <>
          <IconButton
            size="small"
            aria-label="Previous thumbnails"
            onClick={() => handleScrollThumbs("left")}
            disabled={!canScrollLeft}
            className={`thumbs-nav-arrow prev ${!canScrollLeft ? "disabled" : ""}`}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Next thumbnails"
            onClick={() => handleScrollThumbs("right")}
            disabled={!canScrollRight}
            className={`thumbs-nav-arrow next ${!canScrollRight ? "disabled" : ""}`}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </>
      )}

      {zoomOnHover && (
        <FullScreenZoomModal
          open={fullScreenModalOpen}
          onClose={() => setFullScreenModalOpen(false)}
          images={ImagesArr}
          currentIndex={currentSlideIndex}
          onIndexChange={(idx) => setCurrentSlideIndex(idx)}
        />
      )}
    </div>
  );
};

export default ImageCarousel;
